'use client';

import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';
import { getConcertImagePath } from '@v2/services/config';
import { useIsConcerts } from '../use-is-concerts';

const LEAGUES_WITH_HERO_IMAGES = new Set([
  'nfl',
  'nba',
  'mlb',
  'nhl',
  'mls',
  'pga',
  'ufc',
  'nascar',
  'formula-1',
  'ncaa-mens-basketball',
  'ncaa-mens-football',
  'mlb-spring-training',
]);

const LEAGUE_HERO_OVERRIDES: Record<string, string> = {
  wnba: '/events-hero-image/ncaa-mens-basketball.png',
  'european-soccer': '/events-hero-image/european-soccer.jpeg',
};

const useGetImage = (defaultImagePath?: string) => {
  const isConcerts = useIsConcerts();
  const pathname = usePathname();

  const searchParams = useSearchParams();
  const isWhiteLabel = searchParams?.get('partnerSlug');
  if (isConcerts) {
    return getConcertImagePath(pathname || defaultImagePath || 'concerts');
  }

  if (defaultImagePath) {
    if (defaultImagePath.startsWith('/')) {
      return defaultImagePath;
    }

    const slug = defaultImagePath.toLowerCase();

    if (LEAGUE_HERO_OVERRIDES[slug]) {
      return LEAGUE_HERO_OVERRIDES[slug];
    }

    if (LEAGUES_WITH_HERO_IMAGES.has(slug)) {
      return `/events-hero-image/${slug}.png`;
    }

    if (isWhiteLabel && pathname?.includes('/destination')) {
      return defaultImagePath;
    }
  }

  return '/hero-cover.webp';
};

type Props = {
  imagePath?: string;
  hasBgGradient?: boolean;
  heightClassName?: string;
};
export function EventsHeroImage({
  imagePath,
  hasBgGradient = false,
  heightClassName = 'h-[300px] md:h-96',
}: Props) {
  const heroPath = useGetImage(imagePath);

  const isConcerts = useIsConcerts();

  if (isConcerts || hasBgGradient) {
    return (
      <div
        className={`absolute top-0 z-[-1] w-full overflow-hidden ${heightClassName}`}
      >
        <Image
          src={heroPath}
          alt=""
          fill
          priority
          fetchPriority="high"
          sizes="100vw"
          className="object-cover opacity-20 mix-blend-luminosity"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(257.29% 142.04% at 100% 100%, rgba(16, 24, 40, 0) 0%, rgba(16, 24, 40, 0.72) 100%)',
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={`absolute top-0 z-[-1] w-full overflow-hidden ${heightClassName}`}
    >
      <Image
        src={heroPath}
        alt=""
        fill
        priority
        fetchPriority="high"
        sizes="100vw"
        className="object-cover"
      />
    </div>
  );
}
